//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\HR\Pages\DashBoard.jsx

import { useEffect, useMemo, useRef,useState } from "react";
import {
    ClipboardList,
    Users,
    CheckCircle2,
    Hourglass,
    FileText,
    MessageSquare,
    User,
    ShieldCheck,
    ChevronDown,
} from "lucide-react";
import { getClientDashboardData, getDashboardGraphFilterData } from "../Services/CompanyServices";
import { useNavigate } from "react-router-dom";

const ROW_ICONS = [
    { Icon: FileText, iconBg: "bg-purple-100", iconColor: "text-purple-600" },
    { Icon: MessageSquare, iconBg: "bg-purple-100", iconColor: "text-purple-600" },
    { Icon: User, iconBg: "bg-purple-100", iconColor: "text-purple-600" },
    { Icon: ShieldCheck, iconBg: "bg-purple-100", iconColor: "text-purple-600" },
];


const AVATAR_STYLES = [
    { avatarBg: "bg-purple-100", avatarColor: "text-purple-700" },
];

const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const truncateText = (text, maxLength = 15) => {
    if (!text) return "—";

    return text.length > maxLength
        ? `${text.slice(0, maxLength)}...`
        : text;
};

const formatDateTime = (isoString) => {
    if (!isoString) return "—";

    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const ACTIVITY_LEGEND = [
    { key: "invited", label: "Invited", color: "#7f35b2" },
    { key: "started", label: "Started", color: "#c9a3e6" },
    { key: "completed", label: "Completed", color: "#16a34a" },
    { key: "expired", label: "Expired", color: "#ef7c5c" },
];

// TODO: hardcoded placeholder breakdowns — swap for real values once a breakdown API exists
const STAT_BREAKDOWNS = {
    "Total Assessments": [
        { label: "Active", value: 2 },
        { label: "Draft", value: 1 },
        { label: "Archived", value: 0 },
    ],
    "Invited Candidates": [
        { label: "Accepted", value: 4 },
        { label: "Pending", value: 12 },
        { label: "Expired", value: 0 },
    ],
    "Completed Responses": [
        { label: "Reviewed", value: 3 },
        { label: "Shortlisted", value: 1 },
        { label: "Needs review", value: 1 },
    ],
    "Assessments Awaiting Responses": [
        { label: "Due this week", value: 7 },
        { label: "Due later", value: 3 },
        { label: "Overdue", value: 2 },
    ],
};

// API returns 3 ten-day buckets per month (1-10, 11-20, 21-end), with dateRange
// like "2026-09-01 to 2026-09-10". Turn that into a short axis/tooltip label.
const formatIntervalLabel = (dateRange) => {
    if (!dateRange) return "";
    const [startStr, endStr] = dateRange.split(" to ");
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return dateRange;
    const month = start.toLocaleDateString("en-IN", { month: "short" });
    return `${month} ${start.getDate()}–${end.getDate()}`;
};

const buildYTicks = (data) => {
    const maxVal = Math.max(0, ...data.flatMap((d) => [d.invited, d.started, d.completed, d.expired]));
    const roundedMax = Math.max(5, Math.ceil((maxVal || 5) / 5) * 5);
    const step = roundedMax / 4;
    return [0, step, step * 2, step * 3, roundedMax];
};

const MONTH_OPTIONS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 2025;

const YEAR_OPTIONS = Array.from(
    { length: CURRENT_YEAR - MIN_YEAR + 1 },
    (_, index) => CURRENT_YEAR - index
);

export default function DashBoard() {
    const navigate = useNavigate();
    const [countsData, setCountsData] = useState(null);
    const [assignedAssessments, setAssignedAssessments] = useState([]);
    const [recentInvites, setRecentInvites] = useState([]);
    const [activityIntervals, setActivityIntervals] = useState([]);
    const [assessmentStatusBreakdown, setAssessmentStatusBreakdown] = useState(null);
    const [invitedCandidatesBreakdown, setInvitedCandidatesBreakdown] = useState([]);
    const [completedResponsesBreakdown, setCompletedResponsesBreakdown] = useState([]);
    const [awaitingResponsesBreakdown, setAwaitingResponsesBreakdown] = useState([]);
    const [assessmentOptions, setAssessmentOptions] = useState([]);
    const [filterAssessmentUID, setFilterAssessmentUID] = useState("");
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [graphLoading, setGraphLoading] = useState(false);
    const isInitialFilterRender = useRef(true);
    const [loading, setLoading] = useState(false);
    const [hoveredStatLabel, setHoveredStatLabel] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (isInitialFilterRender.current) {
            isInitialFilterRender.current = false;
            return;
        }
        fetchGraphData();
    }, [filterAssessmentUID, filterMonth, filterYear]);

    const fetchGraphData = async () => {
    try {
        setGraphLoading(true);
        const payload = {
            assessmentUID: filterAssessmentUID || null,
            month: filterMonth,
            year: filterYear,
        };
        const response = await getDashboardGraphFilterData(payload);
        if (response.data.status?.toLowerCase() === "success") {
            const graphData = response.data.data || [];
            setActivityIntervals(Array.isArray(graphData) ? graphData : []);
        }
    } catch (error) {
        // keep previous chart data on failure
    } finally {
        setGraphLoading(false);
    }
};

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await getClientDashboardData();
            if (response.data.status?.toLowerCase() === "success") {
                const data = response.data.data || {};
                setCountsData(data.countsData || null);
                setAssignedAssessments(data.topFiveRecentlyAssignedAssessments || []);
                setRecentInvites(data.topFiveRecentCandidateInvites || []);
                setActivityIntervals(data.assessmentActivityIntervals || []);
                setAssessmentStatusBreakdown(data.assessmentStatusBreakdown || null);
                setInvitedCandidatesBreakdown(data.invitedCandidatesBreakdown || []);
                setCompletedResponsesBreakdown(data.completedResponsesBreakdown || []);
                setAwaitingResponsesBreakdown(data.awaitingResponsesBreakdown || []);
                setAssessmentOptions(data.assginedAssessmentList || []);
            } else {
                // eslint-disable-next-line no-undef
                CustomToast("fail", response.data.message || "Unable to load dashboard data.");
            }
        } catch (error) {
            // eslint-disable-next-line no-undef
            CustomToast("fail", error.response?.data?.message || "Unable to load dashboard data.");
        } finally {
            setLoading(false);
        }
    };

    const stats = [
        {
            label: "Total Assessments",
            value: countsData?.totalAssessments ?? "—",
            caption: "Assigned to you",
            icon: ClipboardList,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600",
            viewAllPath: "/Company/dashboard/my-assessments",
        },
        {
            label: "Invited Candidates",
            value: countsData?.invitedCandidates ?? "—",
            caption: "Across all assessments",
            icon: Users,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600",
            viewAllPath: "/Company/dashboard/reports",
        },
        {
            label: "Completed Responses",
            value: countsData?.completedResponses ?? "—",
            caption: "Overall completion",
            icon: CheckCircle2,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600",
            viewAllPath: "/Company/dashboard/reports",

        },
        {
            label: "Assessments Awaiting Responses",
            value: countsData?.awaitingResponses ?? "—",
            caption: "Candidates yet to respond",
            icon: Hourglass,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600",
            viewAllPath: "/Company/dashboard/reports",
        },
    ];
    const statBreakdowns = useMemo(() => {
        const result = { ...STAT_BREAKDOWNS };

        if (assessmentStatusBreakdown) {
            result["Total Assessments"] = [
                { label: "Active", value: assessmentStatusBreakdown.activeCount ?? 0 },
                { label: "Scheduled", value: assessmentStatusBreakdown.scheduledCount ?? 0 },
                { label: "Ended", value: assessmentStatusBreakdown.endedCount ?? 0 },
            ];
        }

        if (invitedCandidatesBreakdown.length > 0) {
            // API groups by channel + experience type — keep both, shown as a two-line label
            const byKey = {};
            invitedCandidatesBreakdown.forEach((entry) => {
                const title = entry.channelName || "Other";
                const subtitle = entry.experienceType || "Other";
                const key = `${title}__${subtitle}`;
                if (!byKey[key]) {
                    byKey[key] = { title, subtitle, value: 0 };
                }
                byKey[key].value += entry.invitedCount ?? 0;
            });
            result["Invited Candidates"] = Object.values(byKey).map(({ title, subtitle, value }) => ({
                label: { title, subtitle },
                value,
            }));
        }

        if (completedResponsesBreakdown.length > 0) {
            const byKey = {};
            completedResponsesBreakdown.forEach((entry) => {
                const title = entry.channelName || "Other";
                const subtitle= entry.experienceType ||" Other";
                const key = `${title}__${subtitle}`;
                if (!byKey[key]) {
                    byKey[key] = { title, subtitle, value: 0 };
                }
                byKey[key].value += entry.completedCount ?? 0;
            });
            result["Completed Responses"] = Object.values(byKey).map(({ title, subtitle, value }) => ({
                label: { title, subtitle },
                value,
            }));
        }
        if (awaitingResponsesBreakdown.length > 0) {
            const byKey = {};
            awaitingResponsesBreakdown.forEach((entry) => {
                const title = entry.channelName || "Other";
                const subtitle= entry.experienceType ||" Other";
                const key = `${title}__${subtitle}`;
                if (!byKey[key]) {
                    byKey[key] = { title, subtitle, value: 0 };
                }
                byKey[key].value += entry.awaitingCount ?? 0;
            });
            result["Assessments Awaiting Responses"] = Object.values(byKey).map(({ title, subtitle, value }) => ({
                label: { title, subtitle },
                value,
            }));
        }

        return result;
    }, [assessmentStatusBreakdown, invitedCandidatesBreakdown, completedResponsesBreakdown]);


    const activityChartData = useMemo(
        () =>
            activityIntervals.map((interval) => ({
                label: formatIntervalLabel(interval.dateRange) || interval.intervalLabel,
                invited: interval.invitedCount ?? 0,
                started: interval.startedCount ?? 0,
                completed: interval.completedCount ?? 0,
                expired: interval.expiredCount ?? 0,
            })),
        [activityIntervals]
    );


    return (
        <div className="flex-1 min-h-screen bg-gray-50/60 px-2 py-1">
            {hoveredStatLabel && (
                <div className="fixed inset-0 z-40 backdrop-blur-sm bg-white/30 pointer-events-none" />
            )}

            {/* <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6 relative"> */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6 relative items-start">
                {stats.map((stat) => (
                    <StatCard
                        key={stat.label}
                        {...stat}
                        navigate={navigate}
                        rows={statBreakdowns[stat.label] || []}
                        isHovered={hoveredStatLabel === stat.label}
                        onMouseEnter={() => setHoveredStatLabel(stat.label)}
                        onMouseLeave={() => setHoveredStatLabel(null)}
                    />
                ))}
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-6 items-stretch">
                <div className="lg:col-span-3">
                    <AssignedAssessmentsCard assessments={assignedAssessments} loading={loading} navigate={navigate} />
                </div>
                 <div className="lg:col-span-2">
                    <AssessmentActivityCard
                        data={activityChartData}
                        loading={graphLoading}
                        assessmentOptions={assessmentOptions}
                        filterAssessmentUID={filterAssessmentUID}
                        filterMonth={filterMonth}
                        filterYear={filterYear}
                        onAssessmentChange={setFilterAssessmentUID}
                        onMonthChange={(v) => setFilterMonth(Number(v))}
                        onYearChange={(v) => setFilterYear(Number(v))}
                    />
                </div>
            </section>

             <RecentInvitesCard invites={recentInvites} loading={loading} navigate={navigate} />
        </div>
    );
}
function StatCard({
    icon: Icon,
    iconBg,
    iconColor,
    label,
    value,
    caption,
    badge,
    badgeBg,
    badgeColor,
    viewAllPath,
    navigate,
    rows = [],
    isHovered,
    onMouseEnter,
    onMouseLeave,
}) {
    const cardBody = (extended) => (
        <>
            {/* Card Header */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div
                        className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}
                    >
                        <Icon
                            className={`w-5 h-5 ${iconColor}`}
                            strokeWidth={2}
                        />
                    </div>

                    <p className="text-sm text-gray-500 truncate">
                        {label}
                    </p>
                </div>

                <ChevronDown
                    className="w-4 h-4 text-black-300 shrink-0 cursor-pointer"
                    strokeWidth={2}
                    onMouseEnter={onMouseEnter}
                    
                />
            </div>

            {/* Value */}
            <p className="text-2xl font-bold text-slate-900 mt-3">
                {value}
            </p>

            {/* Caption + Badge */}
            <div className="flex items-center justify-between gap-2 mt-2">
                <p className="text-xs text-gray-400">
                    {caption}
                </p>

                {badge && (
                    <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${badgeBg} ${badgeColor}`}
                    >
                        {badge}
                    </span>
                )}
            </div>

            {/* Keep original spacing area */}
            {viewAllPath && (
                <div className="flex justify-end mt-3"></div>
            )}

            {extended && (
                <div className="pt-4 mt-1 border-t border-gray-100">
                    <div className="space-y-2">
                        {rows.slice(0, 3).map((row) => {
                            const isStackedLabel = typeof row.label === "object" && row.label !== null;
                            const rowKey = isStackedLabel ? `${row.label.title}-${row.label.subtitle}` : row.label;
                            return (
                                <div
                                    key={rowKey}
                                    className="flex items-center justify-between text-sm"
                                >
                                    {isStackedLabel ? (
                                        <div className="min-w-0">
                                            <p className="font-semibold text-slate-900 truncate">{row.label.title}</p>
                                            <p className="text-xs text-gray-400 truncate">{row.label.subtitle}</p>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500">{row.label}</span>
                                    )}

                                    <span className="font-semibold text-slate-900 shrink-0 ml-3">
                                        {row.value}
                                    </span>
                                </div>
                            );
                        })}

                        {rows.length > 3 && (
                            <div className="flex items-center justify-between text-sm pt-1">
                                <span className="text-gray-400">+{rows.length - 3} more</span>
                                <span className="font-semibold text-slate-900 shrink-0 ml-3">
                                    {rows.slice(3).reduce((sum, row) => sum + (row.value ?? 0), 0)}
                                </span>
                            </div>
                        )}
                    </div>

                    {viewAllPath && (
                        <button
                            type="button"
                            onClick={() => navigate(viewAllPath)}
                            className="w-full mt-4 rounded-xl py-2 text-sm font-semibold hover:opacity-90 transition-opacity"
                            style={{
                                backgroundColor: "#7f35b21a",
                                color: "#7f35b2",
                            }}
                        >
                            View full report →
                        </button>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div className="relative">
            {/* Base card — always in normal flow, reserves the grid cell's space so nothing else ever shifts */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                {cardBody(false)}
            </div>

            {/* Hover overlay — absolutely positioned on top of the base card, so it can grow
                downward over whatever sits below without pushing any other content */}
            {isHovered && (
                <div
                    className="absolute left-0 top-0 w-full bg-white rounded-2xl border border-gray-100 shadow-xl p-5"
                    style={{ zIndex: 50 }}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                >
                    {cardBody(true)}
                </div>
            )}
        </div>
    );
}


const ASSESSMENT_STATUS_STYLES = {
    active: "bg-green-50 text-green-600",
    scheduled: "bg-blue-50 text-blue-600",
    ended: "bg-gray-100 text-gray-500",
    draft: "bg-yellow-50 text-yellow-600",
    archived: "bg-gray-100 text-gray-500",
};

function AssessmentStatusPill({ status }) {
    const styles = ASSESSMENT_STATUS_STYLES[(status || "").toLowerCase()] || "bg-gray-100 text-gray-500";
    return (
        <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${styles}`}>
            {status || "—"}
        </span>
    );
}

function AssignedAssessmentsCard({ assessments, loading, navigate }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 h-full">
            <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-slate-900">Top 5 Assigned Assessments</h2>
                <button
                    type="button"
                    onClick={() => navigate("/Company/dashboard/my-assessments")}
                    className="text-sm font-semibold hover:underline"
                    style={{ color: "#7f35b2" }}
                >
                    View all
                </button>
            </div>

          <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[920px]">
                    <thead>
                        <tr className="text-left text-gray-400 text-xs uppercase tracking-wide border-b border-gray-100">
                            <th className="pb-3 font-medium pr-4">Assessment Name</th>
                            <th className="pb-3 font-medium pr-4">Channel</th>
                            <th className="pb-3 font-medium pr-4">Experience</th>
                            <th className="pb-3 font-medium pr-4">Status</th>
                            <th className="pb-3 font-medium pr-4">Assigned Date</th>
                            <th className="pb-3 font-medium pr-4">Assigned To</th>
                            <th className="pb-3 font-medium pr-4">Assigned By</th>
                            <th className="pb-3 font-medium pr-4 text-right">Responders</th>
                            <th className="pb-3 font-medium text-right">Completed</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {assessments.map((assessment, index) => {
                            const { Icon, iconBg, iconColor } = ROW_ICONS[index % ROW_ICONS.length];
                            return (
                                <tr key={assessment.uniqueKey} className="hover:bg-gray-50/60 transition-colors">
                                    <td className="py-3.5 pr-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
                                                <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={2} />
                                            </div>
                                            <span
                                                className="font-medium text-gray-800 truncate block max-w-[200px]"
                                                title={assessment.assessmentName}
                                            >
                                                {assessment.assessmentName}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">{assessment.channelName || "—"}</td>
                                    <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">{assessment.experienceType || "—"}</td>
                                    <td className="py-3.5 pr-4">
                                        <AssessmentStatusPill status={assessment.assessmentStatus} />
                                    </td>
                                    <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">{formatDateTime(assessment.assignedDate)}</td>
                                    <td className="py-3.5 pr-4 text-gray-600">
                                        <div
                                            className="w-[140px] truncate"
                                            title={assessment.assignedToName || "—"}
                                        >
                                            {assessment.assignedToName || "—"}
                                        </div>
                                    </td>
                                    <td className="py-3.5 pr-4 text-gray-600">
                                        <div
                                            className="w-[140px] truncate"
                                            title={assessment.assignedByName || "—"}
                                        >
                                            {assessment.assignedByName || "—"}
                                        </div>
                                    </td>
                                    <td className="py-3.5 pr-4 text-gray-600 text-right">{assessment.responderCount ?? 0}</td>
                                    <td className="py-3.5 text-gray-600 text-right">{assessment.completedResponderCount ?? 0}</td>
                                </tr>
                            );
                        })}
                        {!loading && assessments.length === 0 && (
                            <tr>
                                <td colSpan={9} className="py-6 text-center text-sm text-gray-400">
                                    No assessments assigned yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

        </div>
    );
}

function AssessmentActivityCard({
    data,
    loading,
    assessmentOptions,
    filterAssessmentUID,
    filterMonth,
    filterYear,
    onAssessmentChange,
    onMonthChange,
    onYearChange,
}) {

    const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
    const yearDropdownRef = useRef(null);
    const [assessmentDropdownOpen, setAssessmentDropdownOpen] = useState(false);
    const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                yearDropdownRef.current &&
                !yearDropdownRef.current.contains(event.target)
            ) {
                setYearDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);
    const selectClass =
        "text-xs font-medium rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-gray-600 " +
        "focus:border-[#7f35b2] focus:outline-none focus:ring-1 focus:ring-[#7f35b2]";

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 h-full flex flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "#7f35b2" }}>Performance</p>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Assessment Activity</h2>

            <div className="flex flex-wrap items-center gap-2 mb-5 ">
               <div className="relative">
                    <button
                        type="button"
                        onClick={() => setAssessmentDropdownOpen((prev) => !prev)}
                        className={`${selectClass} min-w-[145px] flex items-center justify-between gap-2`}
                    >
                        <span className="truncate">
                            {assessmentOptions.find(
                                (a) => a.assessmentUID === filterAssessmentUID
                            )?.assessmentName || "All assessments"}
                        </span>

                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${
                                assessmentDropdownOpen ? "rotate-180" : ""
                            }`}
                        />
                    </button>

                    {assessmentDropdownOpen && (
                        <div className="absolute left-0 top-full mt-1 w-full z-50">
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                <div className="max-h-[108px] overflow-y-auto">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onAssessmentChange("");
                                            setAssessmentDropdownOpen(false);
                                        }}
                                        className="w-full px-3 py-2 text-xs text-left text-gray-600 hover:bg-gray-50"
                                    >
                                        All assessments
                                    </button>

                                    {assessmentOptions.map((a) => (
                                        <button
                                            key={a.assessmentUID}
                                            type="button"
                                            onClick={() => {
                                                onAssessmentChange(a.assessmentUID);
                                                setAssessmentDropdownOpen(false);
                                            }}
                                            className={`w-full px-3 py-2 text-xs text-left ${
                                                filterAssessmentUID === a.assessmentUID
                                                    ? "bg-purple-50 text-[#7f35b2] font-semibold"
                                                    : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {a.assessmentName}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setMonthDropdownOpen((prev) => !prev)}
                        className={`${selectClass} min-w-[105px] flex items-center justify-between gap-2`}
                    >
                        <span>
                            {MONTH_OPTIONS.find((m) => m.value === filterMonth)?.label}
                        </span>

                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${
                                monthDropdownOpen ? "rotate-180" : ""
                            }`}
                        />
                    </button>

                    {monthDropdownOpen && (
                        <div className="absolute left-0 top-full mt-1 w-full z-50">
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                <div className="max-h-[108px] overflow-y-auto">
                                    {MONTH_OPTIONS.map((m) => (
                                        <button
                                            key={m.value}
                                            type="button"
                                            onClick={() => {
                                                onMonthChange(m.value);
                                                setMonthDropdownOpen(false);
                                            }}
                                            className={`w-full px-3 py-2 text-xs text-left ${
                                                Number(filterMonth) === m.value
                                                    ? "bg-purple-50 text-[#7f35b2] font-semibold"
                                                    : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
    
                <div ref={yearDropdownRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setYearDropdownOpen((prev) => !prev)}
                        className={`${selectClass} min-w-[82px] flex items-center justify-between gap-2`}
                    >
                        <span>{filterYear}</span>

                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform `}
                        />
                    </button>

                    {yearDropdownOpen && (
                        <div className="absolute right-0 top-full mt-1 w-[82px] z-50">
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                <div className="max-h-[108px] overflow-y-auto">
                                    {YEAR_OPTIONS.map((year) => (
                                        <button
                                            key={year}
                                            type="button"
                                            onClick={() => {
                                                onYearChange(year);
                                                setYearDropdownOpen(false);
                                            }}
                                            className={`w-full px-3 py-2 text-xs text-left transition-colors ${
                                                Number(filterYear) === year
                                                    ? "bg-purple-50 text-[#7f35b2] font-semibold"
                                                    : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {year}
                                        </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-400" style={{ minHeight: 200 }}>
                    Loading activity…
                </div>
            ) : data.length === 0 ||
            !data.some(
                (point) =>
                    (point.invited ?? 0) > 0 ||
                    (point.started ?? 0) > 0 ||
                    (point.completed ?? 0) > 0 ||
                    (point.expired ?? 0) > 0
            ) ? (
                <div
                    className="flex-1 flex items-center justify-center text-center"
                    style={{ minHeight: 200 }}
                >
                    <div>
                        <p className="text-sm font-semibold text-slate-700">
                            No activity to display
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            There is no assessment activity for the selected period.
                        </p>
                    </div>
                </div>
            ) : (
                <ActivityChart data={data} />
            )}

            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-4">
                {ACTIVITY_LEGEND.map((item) => (
                    <div key={item.key} className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                        <span className="text-xs text-gray-500">{item.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ActivityChart({ data }) {
    const width = 600;
    const height = 200;
    const yTicks = buildYTicks(data);
    const maxY = yTicks[yTicks.length - 1];

    const groupWidth = width / data.length;
    const barGap = 4;
    const barWidth = (groupWidth - barGap * (ACTIVITY_LEGEND.length + 1)) / ACTIVITY_LEGEND.length;

    const [hoveredIndex, setHoveredIndex] = useState(null);
    const hoveredPoint = hoveredIndex !== null ? data[hoveredIndex] : null;

    // Position the tooltip as a percentage across the chart so it roughly follows the hovered group
    const tooltipLeftPct = hoveredIndex !== null ? ((hoveredIndex + 0.5) / data.length) * 100 : 0;

    return (
        <div>
            <div className="flex gap-3 relative">
                 <div className="flex flex-col justify-between text-[11px] text-gray-300" style={{ height }}>
                    {yTicks
                        .slice()
                        .reverse()
                        .map((tick) => (
                            <span key={tick}>{Math.round(tick)}</span>
                        ))}
                </div>

                <div className="relative flex-1">
                    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none">
                         {yTicks.map((tick) => {
                            const y = height - (tick / maxY) * height;
                            return <line key={tick} x1="0" x2={width} y1={y} y2={y} stroke="#F3F4F6" strokeDasharray="4 4" />;
                        })}

                        {data.map((point, groupIndex) => {
                            const groupX = groupIndex * groupWidth;
                            return (
                                <g key={point.label}>
                                    {hoveredIndex === groupIndex && (
                                        <rect
                                            x={groupX}
                                            y={0}
                                            width={groupWidth}
                                            height={height}
                                            fill="#00000010"
                                        />
                                    )}
                                    {ACTIVITY_LEGEND.map((series, seriesIndex) => {
                                        const value = point[series.key] ?? 0;
                                        const barHeight = (value / maxY) * height;
                                        const x = groupX + barGap + seriesIndex * (barWidth + barGap);
                                        const y = height - barHeight;
                                        return (
                                            <rect
                                                key={series.key}
                                                x={x}
                                                y={y}
                                                width={barWidth}
                                                height={barHeight}
                                                rx={2}
                                                fill={series.color}
                                            />
                                        );
                                    })}
                                    {/* Invisible hit area covering the full column height, so hovering anywhere in the group triggers the tooltip */}
                                    <rect
                                        x={groupX}
                                        y={0}
                                        width={groupWidth}
                                        height={height}
                                        fill="transparent"
                                        onMouseEnter={() => setHoveredIndex(groupIndex)}
                                        onMouseLeave={() => setHoveredIndex((prev) => (prev === groupIndex ? null : prev))}
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {hoveredPoint && (
                        <div
                            className="absolute z-10 -translate-x-1/2 -translate-y-full bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 pointer-events-none whitespace-nowrap"
                            style={{ left: `${tooltipLeftPct}%`, top: "-8px" }}
                        >
                            <p className="text-sm font-bold text-slate-900 mb-1.5">{hoveredPoint.label}</p>
                            {ACTIVITY_LEGEND.map((series) => (
                                <p key={series.key} className="text-sm font-medium mb-0.5" style={{ color: series.color }}>
                                    {series.label} : {hoveredPoint[series.key] ?? 0}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex mt-2 pl-8">
                {data.map((point) => (
                    <span
                        key={point.label}
                        className="text-[11px] text-gray-400 text-center"
                        style={{ width: `${100 / data.length}%` }}
                    >
                        {point.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function RecentInvitesCard({ invites, loading, navigate }) {
    const handleViewMore = (invite) => {
        navigate("/Company/dashboard/reports", {
            state: {
                initialFilters: {
                    search: invite.candidateEmail || "",
                    assessment: invite.assessmentName || "",
                },
            },
        });
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-slate-900">Top 5 Recent Candidate Invites</h2>
                <button
                    type="button"
                    onClick={() => navigate("/Company/dashboard/reports")}
                    className="text-sm font-semibold hover:underline"
                    style={{ color: "#7f35b2" }}
                >
                    View all
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1180px]">
                    <thead>
                        <tr className="text-left text-gray-400 text-xs uppercase tracking-wide border-b border-gray-100">
                            <th className="pb-3 font-medium pr-4">Candidate Name</th>
                            {/* <th className="pb-3 font-medium pr-4">Email</th> */}
                            <th className="pb-3 font-medium pr-4">Assessment</th>
                            <th className="pb-3 font-medium pr-4">Channel</th>
                            {/* <th className="pb-3 font-medium pr-4">Experience</th> */}
                            <th className="pb-3 font-medium pr-4">Invited By</th>
                            <th className="pb-3 font-medium pr-4">Invited On</th>
                            <th className="pb-3 font-medium pr-4">Deadline</th>
                            <th className="pb-3 font-medium pr-4">Status</th>
                            <th className="pb-3 font-medium text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {invites.map((invite, index) => {
                            const { avatarBg, avatarColor } = AVATAR_STYLES[index % AVATAR_STYLES.length];
                            const isCompleted = String(invite.isCompleted).toLowerCase() === "true";
                            return (
                                <tr key={invite.responderUID} className="hover:bg-gray-50/60 transition-colors">
                                    <td className="py-3.5 pr-4 max-w-[260px]">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full ${avatarBg} ${avatarColor} flex items-center justify-center text-xs font-semibold shrink-0`}>
                                                {getInitials(invite.candidateName)}
                                            </div>

                                            <div className="min-w-0">
                                                <p
                                                    className="font-medium text-gray-800 truncate"
                                                    title={invite.candidateName}
                                                >
                                                    {truncateText(invite.candidateName, 15)}
                                                </p>

                                                <p
                                                    className="text-xs text-gray-400 truncate"
                                                    title={invite.candidateEmail}
                                                >
                                                    {invite.candidateEmail || "—"}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3.5 pr-4 text-blue-600 font-medium whitespace-nowrap">{invite.assessmentName}</td>
                                    <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">
                                        <div className="min-w-0">
                                                <p
                                                    className="font-medium text-gray-800 truncate"
                                                    title={invite.channel}
                                                >
                                                    {invite.channel || "—"}
                                                </p>

                                                <p
                                                    className="text-xs text-gray-400 truncate"
                                                    title={invite.experienceType}
                                                >
                                                    {invite.experienceType || "—"}
                                                </p>
                                            </div>

                                    </td>
                                    {/* <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">{invite.experienceType || "—"}</td> */}
                                    <td
                                        className="py-3.5 pr-4 text-gray-600"
                                        title={invite.invitedByHRUser || "—"}
                                    >
                                        {truncateText(invite.invitedByHRUser, 15)}
                                    </td>
                                    <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">{formatDateTime(invite.invitedOn)}</td>
                                    <td className="py-3.5 pr-4 text-gray-600 whitespace-nowrap">{formatDateTime(invite.deadline)}</td>
                                    <td className="py-3.5 pr-4">
                                        <StatusPill status={isCompleted ? "Completed" : "Pending"} />
                                    </td>
                                    <td className="py-3.5 text-right">
                                        <button
                                            type="button"
                                            onClick={() => handleViewMore(invite)}
                                            className="text-xs font-semibold hover:underline whitespace-nowrap"
                                            style={{ color: "#7f35b2" }}
                                        >
                                            View More
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {!loading && invites.length === 0 && (
                            <tr>
                                <td colSpan={10} className="py-6 text-center text-sm text-gray-400">
                                    No recent invites.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    // "In Progress" and "Pending" share the same amber treatment in the design
    const styles = status === "Completed" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600";
    return <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${styles}`}>{status}</span>;
}
